import type { SimplifiedPlace } from '@lmn/common-search-widget';
import type { SearchInfoDetailData } from '@/app/search/types.ts';
import { DESTINATION_TYPE, ORIGIN_TYPE } from '@/app/search/types.ts';
import type { LeisureType } from '@/domainLogic/index.ts';
import { LEISURE_TYPES_MAP } from '../constants';

/**
 * Creates a `SimplifiedPlace` object from the `searchInfoDetailData` object.
 *
 * @param searchInfoDetailData - An object containing information about the origin and destination.
 * @returns A `SimplifiedPlace` object compatible with the Common Search Widget, or `undefined`
 *          if the conversion is not possible (e.g., empty input, missing required properties,
 *          or unmapped type).
 *
 * @remarks
 *
 * @example
 * const simplifiedPlace = getCSWPlaceOrigin(searchInfoDetailData);
 * // Returns: { code: 'BER', type: 'Airport' }
 */

export const getCSWPlaceOrigin = (searchInfoDetailData: SearchInfoDetailData): SimplifiedPlace | undefined => {
  const origin = searchInfoDetailData?.origin;
  if (!origin) return undefined;

  const label = origin.description;
  let code;

  if (origin.type === ORIGIN_TYPE.AIRPORT) {
    code = origin.id;
    return { code, label, type: 'Airport' };
  }

  if (origin.type === ORIGIN_TYPE.AIRPORT_GROUP) {
    code = origin.id;
    return { code, label, type: 'Airport' };
  }

  if (origin.type === ORIGIN_TYPE.TRAIN_STATION) {
    code = origin.id;
    return { code, label };
  }

  return origin.id ? { code: origin.id } : undefined;
};

/**
 * Creates a `SimplifiedPlace` object from the `searchInfoDetailData` object.
 *
 * @param searchInfoDetailData - An object containing information about the origin and destination.
 * @returns A `SimplifiedPlace` object compatible with the Common Search Widget, or `undefined`
 *          if the conversion is not possible (e.g., empty input, missing required properties,
 *          or unmapped type).
 *
 * @remarks
 *
 * @example
 * const simplifiedPlace = getCSWPlaceDestination(searchInfoDetailData);
 * // Returns: { code: 'BER', type: 'Airport' }
 */
export const getCSWPlaceDestination = (searchInfoDetailData: SearchInfoDetailData): SimplifiedPlace | undefined => {
  const destination = searchInfoDetailData?.destination;
  if (!destination) return undefined;

  const label = destination.description;
  let code;

  // TODO: Implement TRAIN_STATION
  // TODO: Check GEO as currently it is set as "Anywhere" - is this correct?

  if (destination.destinationType === DESTINATION_TYPE.TAG) {
    code = destination.id.substring(4);
    return { code, label, type: 'Tag' };
  }
  if (destination.destinationType === DESTINATION_TYPE.GEO) {
    code = destination.id;
    return { code, label, type: 'Anywhere' };
  }

  if (destination.destinationType === DESTINATION_TYPE.LEISURE) {
    code = LEISURE_TYPES_MAP[destination.id as LeisureType];
    return { code, label, type: 'Region' };
  }

  if (destination.destinationType === DESTINATION_TYPE.AIRPORT) {
    code = destination.id;
    return { code, label, type: 'Airport' };
  }

  if (destination.destinationType === DESTINATION_TYPE.ANYWHERE) {
    code = destination.id;
    return { code, label, type: 'Anywhere' };
  }

  if (destination.destinationType === DESTINATION_TYPE.CITY) {
    code = destination.id.toString();
    return { code, label, type: 'City' };
  }

  if (destination.destinationType === DESTINATION_TYPE.COUNTRY) {
    code = destination.id;
    return { code, label, type: 'Country' };
  }

  if (destination.destinationType === DESTINATION_TYPE.TRAIN_STATION) {
    code = destination.id;
    return { code, label };
  }

  return undefined;
};
